import type { ReactNode } from "react";

import { cx } from "@/lib/utils";

interface PageHeroProps {
  children: ReactNode;
  /** Inner content container classes (max-width, padding, text-align, grid, …). */
  innerClassName?: string;
  /** Extra classes on the outer <section>. */
  className?: string;
  /** Bottom border under the hero. Defaults to true; pass false for the homepage. */
  bordered?: boolean;
  testId?: string;
}

/**
 * Hero band with the subtle 24×24 grid background and edge fade.
 *
 * Carries the ``st-top`` hook of the storefront themes (KAN-1634), so
 * every page's hero band — not only the landing's — takes the theme's
 * hero background (and, for a dark-hero theme, its dark band). The hook
 * has no effect outside a themed storefront.
 *
 * The grid + radial-gradient mask live on an absolutely-positioned
 * background layer so the fade only thins the grid lines toward the
 * edges — content in the foreground stays at full opacity.
 */
export function PageHero({
  children,
  innerClassName,
  className,
  bordered = true,
  testId,
}: PageHeroProps): JSX.Element {
  return (
    <section
      className={cx(
        "st-top relative overflow-hidden",
        bordered && "border-b border-border",
        className,
      )}
      data-testid={testId}
    >
      <div
        aria-hidden="true"
        className="grid-bg grid-fade pointer-events-none absolute inset-0"
      />
      <div className={cx("relative mx-auto", innerClassName)}>
        {children}
      </div>
    </section>
  );
}
